<script setup lang="ts">
import { ref, computed, watch, readonly } from "vue"
import { Badge, Expandable, Form, Spinner } from "@hantera/design-system"
import { apps } from "@hantera/portal-app"
import useBackend from "@/composition/useBackend"
import useApps from "@/composition/use-apps"
import AppSlotView from "@/AppSlotView.vue"
import type { AppInfo, AppSettingsValues, AppRuntimeState, BrokenDiagnosticKind } from "@/backend/apps.types"
import SettingField from "./SettingField.vue"
import SettingSet from "./SettingSet.vue"
import { layoutSettings } from "./settings-layout"


const props = defineProps<{
  app: AppInfo
}>()

const backend = useBackend()
const appsContext = useApps()


const settingsValues = ref<AppSettingsValues>({})
const editedValues = ref<AppSettingsValues>({})
const isLoadingSettings = ref(false)
const isSavingSettings = ref(false)
const settingsError = ref<string | null>(null)
const settingsSaved = ref(false)

const hasSettings = computed(() => {
  return Object.keys(props.app.settingDefinitions ?? {}).length > 0
})

const settingSets = computed(() => layoutSettings(props.app.settingDefinitions ?? {}))


const hasSettingsSlot = computed(
  () => appsContext.getComponentsFor(apps.appSettingsSlots.settings, props.app.id).length > 0
)

const settingsSlotContext = computed(() => ({
  appId: props.app.id,
  values: readonly(editedValues.value),
  setValue: (key: string, value: unknown) => {
    editedValues.value[key] = value
  },
  refresh: loadSettings,
}))


const requiredGraphNodes = computed(() => {
  const nodes = props.app.requires?.graph?.nodes ?? {}
  return Object.entries(nodes).map(([nodeId, node]) => ({
    nodeId,
    fields: Object.entries(node.fields ?? {}),
    edges: Object.entries(node.edges ?? {}),
  }))
})

const requiredGraphSets = computed(() => {
  const sets = props.app.requires?.graph?.sets ?? {}
  return Object.entries(sets).map(([name, value]) => ({ name, node: value.node }))
})

const requiredModules = computed(() => {
  const modules = props.app.requires?.modules ?? {}
  return Object.entries(modules).map(([path, module]) => ({
    path,
    exports: Object.entries(module.exports ?? {}),
  }))
})

const hasDependencies = computed(() => {
  return (
    requiredGraphNodes.value.length > 0 ||
    requiredGraphSets.value.length > 0 ||
    requiredModules.value.length > 0
  )
})

const providedEntries = computed(() => props.app.provides?.registryEntries ?? [])

const hasProvides = computed(() => providedEntries.value.length > 0)

function formatProvidedValue(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

const runtimeStateLabel: Record<AppRuntimeState, string> = {
  starting: "Starting",
  running: "Running",
  broken: "Broken",
}

const runtimeStateVariant: Record<AppRuntimeState, "primary" | "neutral" | "danger"> = {
  starting: "neutral",
  running: "primary",
  broken: "danger",
}

const diagnosticKindLabel: Record<BrokenDiagnosticKind, string> = {
  moduleType: "Module type",
  graphShape: "Graph shape",
  internal: "Internal",
}

const secretsMarkedForClearing = ref<Set<string>>(new Set())

const hasUnsavedChanges = computed(() => {
  if (secretsMarkedForClearing.value.size > 0) {
    return true
  }
  for (const key of Object.keys(props.app.settingDefinitions ?? {})) {
    const def = props.app.settingDefinitions[key]
    if (def.secret) {
      if (editedValues.value[key] !== undefined && editedValues.value[key] !== "") {
        return true
      }
    } else {
      if (editedValues.value[key] !== settingsValues.value[key]) {
        return true
      }
    }
  }
  return false
})

async function loadSettings() {
  if (!hasSettings.value) return

  isLoadingSettings.value = true
  settingsError.value = null
  secretsMarkedForClearing.value = new Set()
  try {
    settingsValues.value = await backend.apps.getSettings(props.app.id)
    const nonSecretValues: AppSettingsValues = {}
    for (const [key, value] of Object.entries(settingsValues.value)) {
      const def = props.app.settingDefinitions[key]
      if (!def?.secret) {
        nonSecretValues[key] = value
      }
    }
    editedValues.value = nonSecretValues
  } catch (e) {
    settingsError.value = e instanceof Error ? e.message : "Failed to load settings"
  } finally {
    isLoadingSettings.value = false
  }
}

function resetSettings() {
  const nonSecretValues: AppSettingsValues = {}
  for (const [key, value] of Object.entries(settingsValues.value)) {
    const def = props.app.settingDefinitions[key]
    if (!def?.secret) {
      nonSecretValues[key] = value
    }
  }
  editedValues.value = nonSecretValues
  secretsMarkedForClearing.value = new Set()
}

function clearSecret(key: string) {
  secretsMarkedForClearing.value = new Set([...secretsMarkedForClearing.value, key])
  delete editedValues.value[key]
}

function isSecretMarkedForClearing(key: string): boolean {
  return secretsMarkedForClearing.value.has(key)
}

function secretHasStoredValue(key: string): boolean {
  return settingsValues.value[key] === true
}

async function saveSettings() {
  isSavingSettings.value = true
  settingsError.value = null
  settingsSaved.value = false

  try {
    const patch: AppSettingsValues = {}
    for (const key of Object.keys(props.app.settingDefinitions ?? {})) {
      const def = props.app.settingDefinitions[key]
      if (def.secret) {
        if (secretsMarkedForClearing.value.has(key)) {
          patch[key] = null
        } else if (editedValues.value[key] !== undefined && editedValues.value[key] !== "") {
          patch[key] = editedValues.value[key]
        }
      } else {
        if (editedValues.value[key] !== settingsValues.value[key]) {
          patch[key] = editedValues.value[key]
        }
      }
    }

    if (Object.keys(patch).length > 0) {
      await backend.apps.patchSettings(props.app.id, patch)
      await loadSettings()
      settingsSaved.value = true
      setTimeout(() => { settingsSaved.value = false }, 3000)
    }
  } catch (e) {
    settingsError.value = e instanceof Error ? e.message : "Failed to save settings"
  } finally {
    isSavingSettings.value = false
  }
}

watch(() => props.app.id, () => {
  loadSettings()
}, { immediate: true })
</script>

<template>
  <div class="app-details">
    <div class="app-header">
      <div class="app-title-row">
        <h1 class="app-title">{{ app.name }}</h1>
        <Badge
          v-if="app.runtimeState"
          :variant="runtimeStateVariant[app.runtimeState]"
        >
          {{ runtimeStateLabel[app.runtimeState] }}
        </Badge>
      </div>
      <div v-if="app.currentVersion" class="app-version">
        Version {{ app.currentVersion }}
      </div>
    </div>

    <div class="app-content">
      <div>
        <div class="box-content-label">Overview</div>
        <div class="box-content">
          <dl>
            <dt>App ID</dt>
            <dd><code>{{ app.id }}</code></dd>
            <template v-if="app.description" class="detail-item">
              <dt>Description</dt>
              <dd>{{ app.description }}</dd>
            </template>
            <template v-if="app.authors.length > 0" class="detail-item">
              <dt>Authors</dt>
              <dd>{{ app.authors.join(", ") }}</dd>
            </template>
          </dl>
        </div>
      </div>

      <div
        v-if="app.runtimeState === 'broken' && app.diagnostics.length > 0"
        class="diagnostics-section"
      >
        <div class="box-content-label">Diagnostics</div>
        <div class="box-content diagnostics-box">
          <div
            v-for="(d, i) in app.diagnostics"
            :key="i"
            class="diagnostic-item"
          >
            <div class="diagnostic-header">
              <code class="diagnostic-path">{{ d.path }}</code>
              <Badge variant="neutral">{{ diagnosticKindLabel[d.kind] }}</Badge>
            </div>
            <div class="diagnostic-message">{{ d.message }}</div>
          </div>
        </div>
      </div>

      <div v-if="hasSettingsSlot">
        <div class="box-content-label">Configuration</div>
        <div class="box-content settings-box">
          <AppSlotView
            :slot="apps.appSettingsSlots.settings"
            :app-id="app.id"
            :context="settingsSlotContext"
          />
        </div>
      </div>

      <div v-if="hasSettings">
        <div class="box-content-label">Settings</div>
        <div class="box-content settings-box">
          <div v-if="isLoadingSettings" class="settings-loading">
            <Spinner />
            <span>Loading settings...</span>
          </div>
          <div v-else-if="settingsError" class="settings-error">
            {{ settingsError }}
            <button class="btn-link" @click="loadSettings">Retry</button>
          </div>
          <Form v-else :disabled="isSavingSettings" @submit="saveSettings">
            <template v-for="set in settingSets" :key="set.key ?? '__ungrouped'">
              <template v-if="set.key === null">
                <SettingField
                  v-for="setting in set.settings"
                  :key="setting.key"
                  :model-value="editedValues[setting.key]"
                  :definition="setting.definition"
                  :setting-key="setting.key"
                  :has-stored-value="secretHasStoredValue(setting.key)"
                  :marked-for-clearing="isSecretMarkedForClearing(setting.key)"
                  @update:model-value="editedValues[setting.key] = $event"
                  @clear="clearSecret(setting.key)"
                />
              </template>
              <SettingSet v-else :set="set">
                <SettingField
                  v-for="setting in set.settings"
                  :key="setting.key"
                  :model-value="editedValues[setting.key]"
                  :definition="setting.definition"
                  :setting-key="setting.key"
                  :has-stored-value="secretHasStoredValue(setting.key)"
                  :marked-for-clearing="isSecretMarkedForClearing(setting.key)"
                  @update:model-value="editedValues[setting.key] = $event"
                  @clear="clearSecret(setting.key)"
                />
              </SettingSet>
            </template>
            <div class="settings-actions">
              <button
                class="btn-primary"
                type="submit"
                :disabled="!hasUnsavedChanges || isSavingSettings"
              >
                {{ isSavingSettings ? "Saving..." : "Save Settings" }}
              </button>
              <button
                v-if="hasUnsavedChanges && !isSavingSettings"
                class="btn-secondary"
                type="button"
                @click="resetSettings"
              >
                Cancel
              </button>
              <span v-if="settingsSaved" class="settings-saved">
                Settings saved successfully
              </span>
            </div>
          </Form>
        </div>
      </div>

      <div v-if="hasDependencies">


        <div class="box-content-label">Dependencies</div>
        <div class="box-content deps-box">
          <div v-if="requiredGraphNodes.length > 0" class="deps-group">
            <div class="deps-group-title">Graph nodes</div>
            <div
              v-for="node in requiredGraphNodes"
              :key="node.nodeId"
              class="deps-node"
            >
              <code class="deps-node-id">{{ node.nodeId }}</code>
              <ul v-if="node.fields.length > 0" class="deps-list">
                <li v-for="[fieldName, field] in node.fields" :key="'f-' + fieldName">
                  <code>{{ fieldName }}</code>
                  <span class="deps-type">: {{ field.type }}</span>
                  <span v-if="field.dimension" class="deps-meta"> · dimension {{ field.dimension }}</span>
                  <span v-if="field.enumDefinition" class="deps-meta"> · enum {{ field.enumDefinition }}</span>
                </li>
              </ul>
              <ul v-if="node.edges.length > 0" class="deps-list">
                <li v-for="[edgeName, edge] in node.edges" :key="'e-' + edgeName">
                  <code>{{ edgeName }}</code>
                  <span class="deps-type">
                    : {{ edge.cardinality }} → {{ edge.relatedNode }}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="requiredGraphSets.length > 0" class="deps-group">
            <div class="deps-group-title">Graph sets</div>
            <ul class="deps-list">
              <li v-for="set in requiredGraphSets" :key="set.name">
                <code>{{ set.name }}</code>
                <span class="deps-type"> → {{ set.node }}</span>
              </li>
            </ul>
          </div>

          <div v-if="requiredModules.length > 0" class="deps-group">
            <div class="deps-group-title">Modules</div>
            <div
              v-for="module in requiredModules"
              :key="module.path"
              class="deps-node"
            >
              <code class="deps-node-id">{{ module.path }}</code>
              <ul v-if="module.exports.length > 0" class="deps-list">
                <li v-for="[exportName, exp] in module.exports" :key="exportName">
                  <code>{{ exportName }}</code>
                  <span class="deps-type">: {{ exp.type }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div v-if="hasProvides">
        <div class="box-content-label">Provides</div>
        <div class="box-content deps-box">
          <div
            v-for="entry in providedEntries"
            :key="entry.path"
            class="deps-node"
          >
            <code class="deps-node-id">{{ entry.path }}</code>
            <pre class="provides-value"><code>{{ formatProvidedValue(entry.value) }}</code></pre>
          </div>
        </div>
      </div>

      <div class="box-content p">
        <Expandable>
          <template #label>
            Available Versions <Badge variant="primary">{{ app.installedVersions.length }}</Badge>
          </template>
          <template #default>
            <div class="versions-list">
              <div
                v-for="version in app.installedVersions"
                :key="version"
                class="version-item"
                :class="{ 'version-item--active': version === app.currentVersion }"
              >
                <span class="version-number">{{ version }}</span>
                <Badge v-if="version === app.currentVersion" variant="primary">
                  Active
                </Badge>
              </div>
            </div>
          </template>
        </Expandable>
      </div>

    </div>
  </div>
</template>

<style scoped>
.app-details {
  height: 100%;
  overflow: auto;
  padding: 1.5rem;
}

.app-header {
  margin-bottom: 1.5rem;
}

.app-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.app-title {
  margin: 0;
  font-size: 1.5rem;
}

.app-version {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: hsl(var(--color-text-50));
}

.app-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-list {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-item dt {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: hsl(var(--color-40));
  letter-spacing: 0.05em;
}

.detail-item dd {
  margin: 0;
  font-size: 0.875rem;
  color: hsl(var(--color-20));
}

.detail-item code {
  font-family: monospace;
  background-color: hsl(var(--color-90));
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
}

.versions-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.version-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  background-color: hsl(var(--color-95));
  border-radius: 0.25rem;
}

.version-item--active {
  background-color: hsl(var(--color-primary-95));
}

.version-number {
  font-family: monospace;
  font-size: 0.875rem;
}

.settings-box {
  padding: 1rem;
}

.settings-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: hsl(var(--color-50));
  font-size: 0.875rem;
}

.settings-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: hsl(0 70% 45%);
  font-size: 0.875rem;
}

.settings-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid hsl(var(--color-90));
}

.settings-saved {
  font-size: 0.875rem;
  color: hsl(120 50% 40%);
}

.diagnostics-box {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-left: 3px solid hsl(0 70% 45%);
}

.diagnostic-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.diagnostic-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.diagnostic-path {
  font-family: monospace;
  font-size: 0.8125rem;
  background-color: hsl(var(--color-90));
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
}

.diagnostic-message {
  font-size: 0.875rem;
  color: hsl(var(--color-20));
  white-space: pre-wrap;
}

.deps-box {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.deps-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.deps-group-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: hsl(var(--color-40));
  letter-spacing: 0.05em;
}

.deps-node {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  background-color: hsl(var(--color-95));
  border-radius: 0.25rem;
}

.deps-node-id {
  font-family: monospace;
  font-size: 0.875rem;
  font-weight: 600;
}

.deps-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.deps-list li {
  font-size: 0.8125rem;
}

.deps-list code {
  font-family: monospace;
  font-size: 0.8125rem;
}

.deps-type {
  color: hsl(var(--color-30));
}

.deps-meta {
  color: hsl(var(--color-50));
  font-size: 0.75rem;
}

.provides-value {
  margin: 0.25rem 0 0;
  padding: 0.5rem 0.75rem;
  background-color: hsl(var(--color-90));
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
}
</style>
